<template>
  <transition name="fade">
    <div
      v-if="open"
      class="cart-overlay"
      @click.self="$emit('close')"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cart-heading"
    >
      <div class="cart-panel">
        <header class="cart-header">
          <h2 id="cart-heading" class="cart-title">Your cart</h2>
          <button
            class="cart-close"
            @click="$emit('close')"
            aria-label="Close cart"
          >
            ×
          </button>
        </header>

        <div class="cart-body">
          <p v-if="notice" class="cart-notice" role="status">{{ notice }}</p>
          <div v-if="!cart || cart.totalQuantity === 0" class="cart-empty">
            Your cart is empty.
          </div>

          <ul v-else class="cart-lines">
            <li
              v-for="line in cart.lines.nodes"
              :key="line.id"
              class="cart-line"
            >
              <img
                v-if="line.merchandise.image"
                :src="line.merchandise.image.url"
                :alt="line.merchandise.image.altText || ''"
                class="line-img"
              />
              <div class="line-info">
                <p class="line-title">{{ line.merchandise.product.title }}</p>
                <p
                  v-if="
                    line.merchandise.title &&
                    line.merchandise.title.toLowerCase() !== 'default title'
                  "
                  class="line-variant"
                >
                  {{ line.merchandise.title }}
                </p>
                <p
                  v-for="attr in visibleAttrs(line)"
                  :key="attr.key"
                  class="line-attr"
                >
                  {{ attr.key }}: {{ attr.value }}
                </p>

                <!-- Per-item plate inputs -->
                <div
                  v-if="lineRequiresPlate(line) && plateInputs[line.id]"
                  class="cart-plate-section"
                >
                  <label
                    :for="`cart-plate-${line.id}`"
                    class="cart-plate-label"
                  >
                    License Plate <span class="plate-required">*</span>
                  </label>
                  <input
                    :id="`cart-plate-${line.id}`"
                    v-model.trim="plateInputs[line.id].plate1"
                    type="text"
                    class="cart-plate-input"
                    :class="{
                      'cart-plate-error':
                        showPlateErrors && !plateInputs[line.id].plate1,
                    }"
                    placeholder="e.g. ABC123"
                    maxlength="8"
                    autocomplete="off"
                    @input="showPlateErrors = false"
                  />
                  <p
                    v-if="showPlateErrors && !plateInputs[line.id].plate1"
                    class="plate-error-msg"
                    role="alert"
                  >
                    Required
                  </p>

                  <template v-if="extraPlatesCount(line) >= 1">
                    <label
                      :for="`cart-plate2-${line.id}`"
                      class="cart-plate-label"
                    >
                      License Plate 2
                      <span class="plate-optional">(optional)</span>
                    </label>
                    <input
                      :id="`cart-plate2-${line.id}`"
                      v-model.trim="plateInputs[line.id].plate2"
                      type="text"
                      class="cart-plate-input"
                      placeholder="e.g. ABC123"
                      maxlength="8"
                      autocomplete="off"
                    />
                  </template>

                  <template v-if="extraPlatesCount(line) >= 2">
                    <label
                      :for="`cart-plate3-${line.id}`"
                      class="cart-plate-label"
                    >
                      License Plate 3
                      <span class="plate-optional">(optional)</span>
                    </label>
                    <input
                      :id="`cart-plate3-${line.id}`"
                      v-model.trim="plateInputs[line.id].plate3"
                      type="text"
                      class="cart-plate-input"
                      placeholder="e.g. ABC123"
                      maxlength="8"
                      autocomplete="off"
                    />
                  </template>
                </div>

                <div class="line-controls">
                  <button
                    class="qty-btn"
                    @click="$emit('update-qty', line.id, line.quantity - 1)"
                    :aria-label="`Decrease quantity of ${line.merchandise.product.title}`"
                  >
                    −
                  </button>
                  <span class="qty">{{ line.quantity }}</span>
                  <button
                    class="qty-btn"
                    :disabled="lineLimitOne(line)"
                    :title="
                      lineLimitOne(line)
                        ? 'Limit one per order'
                        : lineRequiresPlate(line)
                        ? 'Add another pass (each pass needs its own license plate)'
                        : null
                    "
                    @click="$emit('update-qty', line.id, line.quantity + 1)"
                    :aria-label="
                      lineRequiresPlate(line)
                        ? `Add another ${line.merchandise.product.title}`
                        : `Increase quantity of ${line.merchandise.product.title}`
                    "
                  >
                    +
                  </button>
                  <button
                    class="remove-btn"
                    @click="$emit('remove', line.id)"
                    aria-label="Remove item from cart"
                  >
                    Remove
                  </button>
                </div>
              </div>
              <p class="line-price">
                ${{
                  (
                    parseFloat(line.merchandise.price.amount) * line.quantity
                  ).toFixed(2)
                }}
              </p>
            </li>
          </ul>
        </div>

        <footer v-if="cart && cart.totalQuantity > 0" class="cart-footer">
          <div class="cart-subtotal">
            <span>Subtotal</span>
            <span class="subtotal-amount">
              ${{ parseFloat(cart.cost.subtotalAmount.amount).toFixed(2) }}
            </span>
          </div>
          <p
            v-if="showPlateErrors && !allPlatesValid"
            class="plate-required-msg"
            role="alert"
          >
            Please enter your correct license plate number. It replaces the
            traditional on-arrival fee envelope. This allows our rangers to
            verify your online pass purchase, ensuring an uninterrupted and
            enjoyable visit to our state park. Exclude all empty spaces and
            special characters (e.g. ABC1234)
          </p>
          <button
            class="checkout-btn"
            :disabled="checkoutLoading"
            @click="onCheckout"
          >
            {{ checkoutLoading ? "Updating…" : "Checkout" }}
          </button>
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { shopifyCart } from "../services/shopifyCart";

export default {
  name: "CartDrawer",
  props: {
    open: { type: Boolean, default: false },
    cart: { type: Object, default: null },
    // Optional message shown at the top of the drawer (e.g. "limit one").
    notice: { type: String, default: "" },
  },
  data() {
    return {
      plateInputs: {},
      checkoutLoading: false,
      showPlateErrors: false,
    };
  },
  computed: {
    allPlatesValid() {
      if (!this.cart) return true;
      return this.cart.lines.nodes.every((line) => {
        if (!this.lineRequiresPlate(line)) return true;
        return !!this.plateInputs[line.id]?.plate1;
      });
    },
  },
  watch: {
    cart(newCart) {
      this.syncPlateInputs(newCart);
    },
  },
  mounted() {
    this.syncPlateInputs(this.cart);
  },
  methods: {
    syncPlateInputs(cart) {
      if (!cart) return;
      const currentIds = new Set(cart.lines.nodes.map((l) => l.id));

      // Remove stale entries
      for (const id of Object.keys(this.plateInputs)) {
        if (!currentIds.has(id)) {
          const updated = { ...this.plateInputs };
          delete updated[id];
          this.plateInputs = updated;
        }
      }

      // Add / pre-populate new entries
      for (const line of cart.lines.nodes) {
        if (this.plateInputs[line.id]) continue;
        const get = (key) =>
          line.attributes.find((a) => a.key === key)?.value || "";
        this.plateInputs = {
          ...this.plateInputs,
          [line.id]: {
            plate1: get("License Plate"),
            plate2: get("License Plate 2"),
            plate3: get("License Plate 3"),
          },
        };
      }
    },

    lineLimitOne(line) {
      return (
        line.attributes.find((a) => a.key === "_limitOne")?.value === "true"
      );
    },

    lineRequiresPlate(line) {
      return (
        line.attributes.find((a) => a.key === "_requiresPlate")?.value ===
        "true"
      );
    },

    extraPlatesCount(line) {
      return parseInt(
        line.attributes.find((a) => a.key === "_extraPlates")?.value || "0",
        10
      );
    },

    visibleAttrs(line) {
      return line.attributes.filter(
        (a) =>
          !a.key.startsWith("_") &&
          a.key !== "License Plate" &&
          a.key !== "License Plate 2" &&
          a.key !== "License Plate 3"
      );
    },

    async onCheckout() {
      if (!this.allPlatesValid) {
        this.showPlateErrors = true;
        return;
      }
      this.checkoutLoading = true;
      try {
        // Limit-one items (annual pass) can never check out with more than 1.
        for (const line of this.cart.lines.nodes) {
          if (this.lineLimitOne(line) && line.quantity > 1) {
            await shopifyCart.updateLineQuantity(line.id, 1);
          }
        }

        // Update each line's attributes with its plates. cartLinesUpdate
        // replaces the full attribute list, so carry over everything that
        // isn't a plate (including the hidden _limitOne/_requiresPlate/
        // _extraPlates/_lineKey flags) or the limit-one check breaks after
        // checkout.
        for (const line of this.cart.lines.nodes) {
          if (!this.lineRequiresPlate(line)) continue;
          const inputs = this.plateInputs[line.id];
          const attrs = line.attributes.filter(
            (a) => !a.key.startsWith("License Plate")
          );
          attrs.push({
            key: "License Plate",
            value: inputs.plate1.toUpperCase(),
          });
          if (this.extraPlatesCount(line) >= 1 && inputs.plate2)
            attrs.push({
              key: "License Plate 2",
              value: inputs.plate2.toUpperCase(),
            });
          if (this.extraPlatesCount(line) >= 2 && inputs.plate3)
            attrs.push({
              key: "License Plate 3",
              value: inputs.plate3.toUpperCase(),
            });
          await shopifyCart.updateLineAttributes(line.id, attrs);
        }

        // Update cart-level attributes (license1, license2, ...) for order fulfillment
        const allPlates = [];
        for (const line of this.cart.lines.nodes) {
          if (!this.lineRequiresPlate(line)) continue;
          const inputs = this.plateInputs[line.id];
          if (inputs.plate1) allPlates.push(inputs.plate1.toUpperCase());
          if (inputs.plate2) allPlates.push(inputs.plate2.toUpperCase());
          if (inputs.plate3) allPlates.push(inputs.plate3.toUpperCase());
        }
        if (allPlates.length) {
          await shopifyCart.updateAttributes(
            allPlates.map((v, i) => ({ key: `license${i + 1}`, value: v }))
          );
        }

        window.location.href = this.cart.checkoutUrl;
      } finally {
        this.checkoutLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.cart-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  display: flex;
  justify-content: flex-end;
}

.cart-panel {
  background: #fff;
  width: 100%;
  max-width: 450px;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.15);
}

.cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #eee;
}

.cart-title {
  font-family: "boldRoboto", sans-serif;
  font-size: 20px;
  margin: 0;
  color: #4c4c4c;
}

.cart-close {
  background: none;
  border: none;
  font-size: 32px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 12px;
  color: #4c4c4c;
}

.cart-close:focus {
  outline: 2px solid #727a35;
  outline-offset: 2px;
  border-radius: 4px;
}

.cart-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
}

.cart-empty {
  text-align: center;
  color: #888;
  padding: 40px 0;
  font-family: "Roboto", sans-serif;
}

.cart-lines {
  list-style: none;
  padding: 0;
  margin: 0;
}

.cart-line {
  display: grid;
  grid-template-columns: 70px 1fr auto;
  gap: 12px;
  padding: 16px 0;
  border-bottom: 1px solid #f0f0f0;
}

.line-img {
  width: 70px;
  height: 70px;
  object-fit: cover;
  border-radius: 4px;
}

.line-info {
  display: flex;
  flex-direction: column;
}

.line-title {
  font-family: "boldRoboto", sans-serif;
  font-size: 14px;
  margin: 0 0 4px 0;
  color: #4c4c4c;
}

.line-variant {
  font-size: 13px;
  color: #666;
  margin: 0 0 4px 0;
}

.line-attr {
  font-size: 12px;
  color: #727a35;
  margin: 0;
  font-style: italic;
}

.cart-plate-section {
  margin: 8px 0 4px;
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.cart-plate-label {
  font-size: 12px;
  font-weight: bold;
  color: #4c4c4c;
}

.plate-required {
  color: #e30a02;
}

.plate-optional {
  color: #888;
  font-weight: normal;
}

.cart-plate-input {
  padding: 5px 8px;
  border: 1px solid #c4c4c4;
  border-radius: 4px;
  font-family: "Roboto", sans-serif;
  font-size: 13px;
  text-transform: uppercase;
  width: 100%;
  box-sizing: border-box;
}

.cart-plate-input:focus {
  outline: 2px solid #727a35;
  outline-offset: 1px;
}

.cart-plate-error {
  border-color: #e30a02;
}

.plate-error-msg {
  color: #e30a02;
  font-size: 12px;
  margin: 0;
}

.line-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
}

.qty-btn {
  background: #f5f5f5;
  border: 1px solid #c4c4c4;
  width: 28px;
  height: 28px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
}

.qty-btn:hover:not(:disabled),
.qty-btn:focus:not(:disabled) {
  background: #e0e0e0;
}

.qty-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.cart-notice {
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  color: #4c4c4c;
  background: #409fae10;
  border: 1px solid #409fae;
  border-radius: 4px;
  padding: 10px 12px;
  margin: 0 0 12px;
}

.qty {
  font-family: "Roboto", sans-serif;
  font-size: 14px;
  min-width: 20px;
  text-align: center;
}

.remove-btn {
  background: none;
  border: none;
  color: #e30a02;
  font-size: 12px;
  cursor: pointer;
  text-decoration: underline;
  padding: 4px 8px;
  margin-left: 8px;
}

.line-price {
  font-family: "boldRoboto", sans-serif;
  font-size: 14px;
  color: #4c4c4c;
  margin: 0;
}

.cart-footer {
  padding: 16px 24px;
  border-top: 1px solid #eee;
  background: #fafafa;
}

.cart-subtotal {
  display: flex;
  justify-content: space-between;
  font-family: "boldRoboto", sans-serif;
  font-size: 16px;
  margin-bottom: 12px;
  color: #4c4c4c;
}

.subtotal-amount {
  font-size: 18px;
}

.plate-required-msg {
  font-family: "Roboto", sans-serif;
  color: #e30a02;
  background: #e30a0210;
  border: 1px solid #e30a02;
  border-radius: 4px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.4;
  margin: 0 0 12px;
  text-align: left;
}

.checkout-btn {
  width: 100%;
  background-color: #727a35;
  color: #fff;
  border: none;
  border-radius: 40px;
  padding: 14px;
  font-family: "Roboto", sans-serif;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
}

.checkout-btn:hover:not(:disabled),
.checkout-btn:focus:not(:disabled) {
  background-color: #87913e;
}

.checkout-btn:disabled {
  background-color: #9f9f9f;
  cursor: not-allowed;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
